import { useToast } from '@/hooks/use-toast';
import {
  createTemplateField,
  createTemplateScreen,
  deleteTemplateField,
  deleteTemplateScreen,
  getFieldsByScreenId,
  getScreensByTemplateId,
  updateTemplateField,
  updateTemplateScreen,
} from '@/modules/templates/services/templates.service';
import { syncFieldSave, syncScreenSave } from '../lib/sync-template-uilm';
import { slugToCamelCase } from '@/i18n/utils/slug-to-camel-case';
import { ensureModuleExistenceLoaded } from '../lib/uilm-existence-cache';
import { getOrCreateTemplateModule } from '../lib/uilm-module-discovery';
import { clearUnpublished } from '../lib/unpublished-tracker';
import { useUnpublishedTracker } from '../hooks/use-unpublished-tracker';
import { generateUilmFile } from '../services/uilm-admin.service';
import type {
  TemplateField,
  TemplateFieldUpdateInput,
  TemplateScreen,
  TemplateScreenUpdateInput,
  UdsTemplateField,
  UdsTemplateScreen,
} from '@/modules/templates/types/templates.types';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui-kit/sheet';
import { BuilderHeader } from './builder-header';
import { NavigationPanel } from './navigation-panel';
import { PreviewCanvas } from './preview-canvas';
import { PreviewDialog } from './preview-dialog';
import { PropertiesPanel } from './properties-panel';

// Types
export interface ScreenWithFields extends TemplateScreen {
  fields: TemplateField[];
}

export interface Template {
  id: string;
  slug: string;
  title: string;
  description: string;
  available: boolean;
  previewToken?: string | null;
  /** Falsy → system template; the UILM sync flow only runs for system templates. */
  organizationId?: string | null;
}

export type SelectionType = 'screen' | 'field' | null;

export interface Selection {
  type: SelectionType;
  screenId: string | null;
  fieldId: string | null;
}

// Context for sharing state across panels
interface BuilderContextType {
  template: Template;
  screens: ScreenWithFields[];
  selection: Selection;
  setSelection: (selection: Selection) => void;
  refreshScreens: () => Promise<void>;
  updateField: (fieldId: string, updates: Partial<TemplateField>) => Promise<void>;
  updateScreen: (screenId: string, updates: Partial<TemplateScreen>) => Promise<void>;
  addScreen: () => void;
  addField: (screenId: string) => void;
  deleteScreen: (screenId: string) => Promise<void>;
  deleteField: (fieldId: string) => Promise<void>;
  reorderScreens: (screenIds: string[]) => Promise<void>;
  reorderFields: (screenId: string, fieldIds: string[]) => Promise<void>;
  saving: boolean;
}

const BuilderContext = createContext<BuilderContextType | null>(null);

export function useBuilder() {
  const context = useContext(BuilderContext);
  if (!context) {
    throw new Error('useBuilder must be used within TypeformBuilder');
  }
  return context;
}

// Helper: convert UDS PascalCase screen to camelCase
const toScreen = (s: UdsTemplateScreen): TemplateScreen => ({
  id: s.ItemId,
  createdAt: s.CreatedDate,
  updatedAt: s.LastUpdatedDate,
  createdBy: s.CreatedBy,
  lastUpdatedBy: s.LastUpdatedBy,
  isDeleted: s.IsDeleted,
  organizationIds: s.OrganizationIds,
  tags: s.Tags,
  templateId: s.TemplateId,
  title: s.Title,
  description: s.Description,
  order: s.Order,
  type: s.Type,
  aiPrompt: s.AiPrompt,
  aiOutputSchema: s.AiOutputSchema,
  dynamicMaxFields: s.DynamicMaxFields,
  dynamicPrompt: s.DynamicPrompt,
  signatoryConfig: s.SignatoryConfig,
  uilmDescriptionKey: s.UilmDescriptionKey,
  uilmTitleKey: s.UilmTitleKey,
  enableApplyStandards: s.EnableApplyStandards,
  conditions: s.Conditions,
});

// Helper: convert UDS PascalCase field to camelCase
const toField = (f: UdsTemplateField): TemplateField => ({
  id: f.ItemId,
  createdAt: f.CreatedDate,
  updatedAt: f.LastUpdatedDate,
  createdBy: f.CreatedBy,
  lastUpdatedBy: f.LastUpdatedBy,
  isDeleted: f.IsDeleted,
  organizationIds: f.OrganizationIds,
  tags: f.Tags,
  screenId: f.ScreenId,
  name: f.Name,
  label: f.Label,
  type: f.Type,
  required: f.Required,
  placeholder: f.Placeholder,
  helpText: f.HelpText,
  options: f.Options,
  order: f.Order,
  aiSuggestionEnabled: f.AiSuggestionEnabled,
  aiSuggestionKey: f.AiSuggestionKey,
  uilmHelpTextKey: f.UilmHelpTextKey,
  uilmLabelKey: f.UilmLabelKey,
  uilmOptionsKeys: f.UilmOptionsKeys,
  uilmPlaceholderKey: f.UilmPlaceholderKey,
  conditions: f.Conditions,
});

// Field `name` is the key the wizard binds form state to (formData[field.name])
// and the placeholder key used during document generation, so it MUST be unique
// across the whole template — two fields sharing a name make the wizard write
// both from one input. These helpers keep generated names collision-free.
function collectFieldNames(screens: ScreenWithFields[]): Set<string> {
  const names = new Set<string>();
  for (const screen of screens) {
    for (const field of screen.fields) {
      if (field.name) names.add(field.name);
    }
  }
  return names;
}

/** Smallest `field{n}` not already present in `used`. */
function nextUniqueFieldName(used: Set<string>): string {
  let n = used.size + 1;
  while (used.has(`field${n}`)) n++;
  return `field${n}`;
}

interface TypeformBuilderProps {
  template: Template;
  initialScreens: ScreenWithFields[];
}

export function TypeformBuilder({ template, initialScreens }: TypeformBuilderProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { toast } = useToast();

  const [screens, setScreens] = useState<ScreenWithFields[]>(initialScreens);
  const [selection, setSelection] = useState<Selection>({
    type: initialScreens.length > 0 ? 'screen' : null,
    screenId: initialScreens[0]?.id || null,
    fieldId: null,
  });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(template.previewToken || null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [propsSheetOpen, setPropsSheetOpen] = useState(false);

  // Mobile-only 3-layer wizard. Desktop ignores this and renders all panels
  // side-by-side. Always lands at L1 (screens) on first mount.
  const [mobileView, setMobileView] = useState<'screens' | 'fields' | 'canvas'>('screens');

  // Keep mobileView consistent if selection vanishes (e.g. screen/field
  // deleted while user was on L2/L3). Pop up to the deepest still-valid
  // layer so we never render an empty canvas or fields list.
  useEffect(() => {
    if (mobileView === 'canvas' && !selection.fieldId) {
      setMobileView(selection.screenId ? 'fields' : 'screens');
    } else if (mobileView === 'fields' && !selection.screenId) {
      setMobileView('screens');
    }
  }, [mobileView, selection.screenId, selection.fieldId]);
  const hasUnpublished = useUnpublishedTracker(template.slug);

  // Ref-mirror of `screens` so the debounced UILM flush below can read the
  // latest values without being re-created on every render. The debounce
  // captures this ref once; useEffect keeps it in sync.
  const screensRef = useRef<ScreenWithFields[]>(screens);
  useEffect(() => {
    screensRef.current = screens;
  }, [screens]);

  // Pre-warm the UILM existence cache on mount. The cache stores which
  // keys are already on UILM for this template's module; new keys then
  // skip `Key/GetsByKeyNames` and save straight via `Key/Save`. Without
  // this pre-warm the first per-entity sync after page load pays the
  // load cost; with it, every save is one round trip from the start.
  // Soft-fails — the cache populates lazily on first sync if this errors.
  useEffect(() => {
    if (template.organizationId) return; // org templates don't sync
    let cancelled = false;
    (async () => {
      try {
        const moduleId = await getOrCreateTemplateModule(template.slug);
        if (cancelled) return;
        await ensureModuleExistenceLoaded(moduleId, slugToCamelCase(template.slug));
      } catch {
        // Lazy-load fallback inside `pushKeysPreservingNonEn` will retry.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [template.slug, template.organizationId]);

  // ---------------------------------------------------------------------
  // Deferred UILM sync.
  //
  // Every `updateField` / `updateScreen` call queues the entity's id and
  // re-arms a 1500ms debounced flush. A burst of typing on the placeholder
  // (debounced at 500ms inside properties-panel) used to fire one full
  // UILM sync per pause; with this queue they collapse into one sync per
  // entity after the admin stops editing. UDS writes still go through
  // immediately — only the UILM half is deferred.
  //
  // 1500ms was chosen to comfortably out-wait the 500ms input debounce in
  // properties-panel so the queue picks up the final value once.
  // ---------------------------------------------------------------------
  const pendingFieldUilmIds = useRef<Set<string>>(new Set());
  const pendingScreenUilmIds = useRef<Set<string>>(new Set());

  // Names of in-flight field creations not yet reflected in `screens`, so rapid
  // "Add field" clicks (which read stale state before refreshScreens lands)
  // can't compute the same name twice.
  const reservedFieldNamesRef = useRef<Set<string>>(new Set());
  // Guards the one-time duplicate-name repair so it runs once per mount.
  const repairedDuplicatesRef = useRef(false);

  const flushPendingFieldUilm = useDebouncedCallback(async () => {
    if (template.organizationId) return;
    const ids = Array.from(pendingFieldUilmIds.current);
    pendingFieldUilmIds.current.clear();
    for (const fieldId of ids) {
      const field = screensRef.current.flatMap((s) => s.fields).find((f) => f.id === fieldId);
      if (!field) continue;
      try {
        await syncFieldSave({
          templateSlug: template.slug,
          screenId: field.screenId,
          fieldId,
          label: field.label,
          helpText: field.helpText,
          placeholder: field.placeholder,
          type: field.type,
          options: field.options ?? [],
        });
      } catch (uilmErr) {
        console.warn('UILM sync deferred for field update:', uilmErr);
      }
    }
  }, 1500);

  const flushPendingScreenUilm = useDebouncedCallback(async () => {
    if (template.organizationId) return;
    const ids = Array.from(pendingScreenUilmIds.current);
    pendingScreenUilmIds.current.clear();
    for (const screenId of ids) {
      const screen = screensRef.current.find((s) => s.id === screenId);
      if (!screen) continue;
      try {
        await syncScreenSave({
          templateSlug: template.slug,
          screenId,
          title: screen.title,
          description: screen.description,
        });
      } catch (uilmErr) {
        console.warn('UILM sync deferred for screen update:', uilmErr);
      }
    }
  }, 1500);

  // Drain pending UILM syncs on unmount so a final edit doesn't strand the
  // UDS state ahead of UILM. The flush fires the async work; we don't
  // await it here because cleanup must be synchronous — UDS already saved
  // so the data is safe; UILM will catch up before the next render path
  // that consumes it.
  useEffect(() => {
    return () => {
      flushPendingFieldUilm.flush();
      flushPendingScreenUilm.flush();
    };
  }, [flushPendingFieldUilm, flushPendingScreenUilm]);

  // Republish the cached UILM file for this template's module. Drains any
  // queued per-entity UILM syncs first so generate runs against the
  // freshest keys — otherwise admin sees the last edit "missing" until
  // the next debounce flush + publish.
  const handlePublishUilm = useCallback(async () => {
    if (template.organizationId) return; // not applicable to org templates
    setPublishing(true);
    try {
      await flushPendingFieldUilm.flush();
      await flushPendingScreenUilm.flush();
      const moduleId = await getOrCreateTemplateModule(template.slug);
      await generateUilmFile(moduleId);
      clearUnpublished(template.slug);
      toast({
        variant: 'success',
        title: t('TEMPLATEBUILDER_TOASTS_UILMPUBLISHED'),
      });
    } catch (err) {
      console.error('Publish failed:', err);
      toast({
        variant: 'destructive',
        title: t('TEMPLATEBUILDER_ERRORS_PUBLISHUILM'),
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setPublishing(false);
    }
  }, [
    template.slug,
    template.organizationId,
    toast,
    t,
    flushPendingFieldUilm,
    flushPendingScreenUilm,
  ]);

  // Get selected screen and field
  const selectedScreen = screens.find((s) => s.id === selection.screenId);
  const selectedField = selectedScreen?.fields.find((f) => f.id === selection.fieldId);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          navigatePrevious();
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateNext();
          break;
        case 'Escape':
          // Go up to screen level if on field
          if (selection.fieldId && selection.screenId) {
            setSelection({
              type: 'screen',
              screenId: selection.screenId,
              fieldId: null,
            });
          }
          break;
        case 'Delete':
        case 'Backspace':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            if (selection.fieldId) {
              deleteField(selection.fieldId);
            } else if (selection.screenId) {
              deleteScreen(selection.screenId);
            }
          }
          break;
        case 'n':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            if (selection.screenId) {
              addField(selection.screenId);
            } else {
              addScreen();
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selection]);

  // Navigation helpers
  const navigatePrevious = () => {
    if (!selectedScreen) return;

    if (selectedField) {
      const fieldIndex = selectedScreen.fields.findIndex((f) => f.id === selectedField.id);
      if (fieldIndex > 0) {
        setSelection({
          type: 'field',
          screenId: selectedScreen.id,
          fieldId: selectedScreen.fields[fieldIndex - 1].id,
        });
      } else {
        setSelection({
          type: 'screen',
          screenId: selectedScreen.id,
          fieldId: null,
        });
      }
    } else {
      const screenIndex = screens.findIndex((s) => s.id === selectedScreen.id);
      if (screenIndex > 0) {
        const prevScreen = screens[screenIndex - 1];
        if (prevScreen.fields.length > 0) {
          setSelection({
            type: 'field',
            screenId: prevScreen.id,
            fieldId: prevScreen.fields[prevScreen.fields.length - 1].id,
          });
        } else {
          setSelection({
            type: 'screen',
            screenId: prevScreen.id,
            fieldId: null,
          });
        }
      }
    }
  };

  const navigateNext = () => {
    if (!selectedScreen) return;

    if (selectedField) {
      const fieldIndex = selectedScreen.fields.findIndex((f) => f.id === selectedField.id);
      if (fieldIndex < selectedScreen.fields.length - 1) {
        setSelection({
          type: 'field',
          screenId: selectedScreen.id,
          fieldId: selectedScreen.fields[fieldIndex + 1].id,
        });
      } else {
        const screenIndex = screens.findIndex((s) => s.id === selectedScreen.id);
        if (screenIndex < screens.length - 1) {
          setSelection({
            type: 'screen',
            screenId: screens[screenIndex + 1].id,
            fieldId: null,
          });
        }
      }
    } else {
      if (selectedScreen.fields.length > 0) {
        setSelection({
          type: 'field',
          screenId: selectedScreen.id,
          fieldId: selectedScreen.fields[0].id,
        });
      } else {
        const screenIndex = screens.findIndex((s) => s.id === selectedScreen.id);
        if (screenIndex < screens.length - 1) {
          setSelection({
            type: 'screen',
            screenId: screens[screenIndex + 1].id,
            fieldId: null,
          });
        }
      }
    }
  };

  // Fetch screens from UDS and merge fields
  const refreshScreens = useCallback(async () => {
    try {
      const screensResult = await getScreensByTemplateId(template.id);
      const rawScreens: UdsTemplateScreen[] = screensResult.items || [];

      if (rawScreens.length === 0) {
        setScreens([]);
        return;
      }

      // Fetch fields for all screens in parallel
      const fieldResults = await Promise.all(rawScreens.map((s) => getFieldsByScreenId(s.ItemId)));

      // Build screens with nested fields, sorted by order
      const mergedScreens: ScreenWithFields[] = rawScreens
        .map((rawScreen, idx) => {
          const rawFields: UdsTemplateField[] = fieldResults[idx].items || [];
          return {
            ...toScreen(rawScreen),
            fields: rawFields.map(toField).sort((a, b) => a.order - b.order),
          };
        })
        .sort((a, b) => a.order - b.order);

      setScreens(mergedScreens);
    } catch (error) {
      toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_REFRESHSCREENS') });
    }
  }, [template.id, t, toast]);

  // One-time repair of pre-existing duplicate field names. Two fields sharing a
  // name make the wizard (which binds form state to formData[field.name]) write
  // both from a single input. We keep the first occurrence and rename later
  // duplicates to a fresh unique name. Runs once on mount; new duplicates are
  // prevented at creation by `addField`.
  const repairDuplicateFieldNames = useCallback(async () => {
    const current = screensRef.current;
    const used = collectFieldNames(current);
    const seen = new Set<string>();
    const renames: { id: string; name: string }[] = [];

    for (const screen of current) {
      for (const field of screen.fields) {
        if (!field.name) continue;
        if (seen.has(field.name)) {
          const newName = nextUniqueFieldName(used);
          used.add(newName);
          seen.add(newName);
          renames.push({ id: field.id, name: newName });
        } else {
          seen.add(field.name);
        }
      }
    }

    if (renames.length === 0) return;
    try {
      // Field name isn't a translatable UILM key, so no UILM sync is needed.
      await Promise.all(renames.map((r) => updateTemplateField(r.id, { Name: r.name })));
      await refreshScreens();
    } catch (err) {
      console.warn('Failed to repair duplicate field names:', err);
    }
  }, [refreshScreens]);

  useEffect(() => {
    if (repairedDuplicatesRef.current) return;
    repairedDuplicatesRef.current = true;
    void repairDuplicateFieldNames();
  }, [repairDuplicateFieldNames]);

  // Update a field
  const updateFieldHandler = useCallback(
    async (fieldId: string, updates: Partial<TemplateField>) => {
      setSaving(true);

      // Keep field names unique template-wide — the wizard binds form state to
      // formData[field.name], so a collision would link two fields. If a rename
      // collides with another field, suffix it to keep it unique.
      if (updates.name) {
        const taken = collectFieldNames(screensRef.current);
        const self = screensRef.current.flatMap((s) => s.fields).find((f) => f.id === fieldId);
        if (self?.name) taken.delete(self.name);
        if (taken.has(updates.name)) {
          let suffix = 2;
          while (taken.has(`${updates.name}_${suffix}`)) suffix++;
          updates = { ...updates, name: `${updates.name}_${suffix}` };
        }
      }

      try {
        // Map camelCase updates to PascalCase for UDS
        const pascalInput: TemplateFieldUpdateInput = {};
        if (updates.name !== undefined) pascalInput.Name = updates.name;
        if (updates.label !== undefined) pascalInput.Label = updates.label;
        if (updates.type !== undefined) pascalInput.Type = updates.type;
        if (updates.required !== undefined) pascalInput.Required = updates.required;
        if (updates.placeholder !== undefined) pascalInput.Placeholder = updates.placeholder;
        if (updates.helpText !== undefined) pascalInput.HelpText = updates.helpText;
        if (updates.options !== undefined) pascalInput.Options = updates.options;
        if (updates.order !== undefined) pascalInput.Order = updates.order;
        if (updates.aiSuggestionEnabled !== undefined)
          pascalInput.AiSuggestionEnabled = updates.aiSuggestionEnabled;
        if (updates.aiSuggestionKey !== undefined)
          pascalInput.AiSuggestionKey = updates.aiSuggestionKey;
        if (updates.uilmHelpTextKey !== undefined)
          pascalInput.UilmHelpTextKey = updates.uilmHelpTextKey;
        if (updates.uilmLabelKey !== undefined) pascalInput.UilmLabelKey = updates.uilmLabelKey;
        if (updates.uilmOptionsKeys !== undefined)
          pascalInput.UilmOptionsKeys = updates.uilmOptionsKeys;
        if (updates.uilmPlaceholderKey !== undefined)
          pascalInput.UilmPlaceholderKey = updates.uilmPlaceholderKey;
        if (updates.conditions !== undefined) pascalInput.Conditions = updates.conditions;
        if (updates.screenId !== undefined) pascalInput.ScreenId = updates.screenId;

        await updateTemplateField(fieldId, pascalInput);

        // Optimistically update local state
        setScreens((prev) =>
          prev.map((screen) => ({
            ...screen,
            fields: screen.fields.map((field) =>
              field.id === fieldId ? { ...field, ...updates } : field
            ),
          }))
        );

        // Queue a deferred UILM sync — only when a translatable field
        // changed. Type changes count because the option-keys structure
        // can change with type (text → select adds option keys; select
        // → text drops them). The actual sync runs ~1500ms after the
        // last edit settles; many keystrokes in a row coalesce to one.
        const touchedTranslatable =
          updates.label !== undefined ||
          updates.helpText !== undefined ||
          updates.placeholder !== undefined ||
          updates.options !== undefined ||
          updates.type !== undefined;
        if (!template.organizationId && touchedTranslatable) {
          pendingFieldUilmIds.current.add(fieldId);
          flushPendingFieldUilm();
        }
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_SAVECHANGES') });
        await refreshScreens();
      } finally {
        setSaving(false);
        setLastSaved(new Date());
      }
    },
    [
      template.organizationId,
      refreshScreens,
      t,
      toast,
      flushPendingFieldUilm,
    ]
  );

  // Update a screen
  const updateScreenHandler = useCallback(
    async (screenId: string, updates: Partial<TemplateScreen>) => {
      setSaving(true);
      try {
        // Map camelCase updates to PascalCase for UDS
        const pascalInput: TemplateScreenUpdateInput = {};
        if (updates.title !== undefined) pascalInput.Title = updates.title;
        if (updates.description !== undefined) pascalInput.Description = updates.description;
        if (updates.order !== undefined) pascalInput.Order = updates.order;
        if (updates.type !== undefined) pascalInput.Type = updates.type;
        if (updates.aiPrompt !== undefined) pascalInput.AiPrompt = updates.aiPrompt;
        if (updates.aiOutputSchema !== undefined)
          pascalInput.AiOutputSchema = updates.aiOutputSchema;
        if (updates.dynamicMaxFields !== undefined)
          pascalInput.DynamicMaxFields = updates.dynamicMaxFields;
        if (updates.dynamicPrompt !== undefined) pascalInput.DynamicPrompt = updates.dynamicPrompt;
        if (updates.signatoryConfig !== undefined)
          pascalInput.SignatoryConfig = updates.signatoryConfig;
        if (updates.uilmDescriptionKey !== undefined)
          pascalInput.UilmDescriptionKey = updates.uilmDescriptionKey;
        if (updates.uilmTitleKey !== undefined) pascalInput.UilmTitleKey = updates.uilmTitleKey;
        if (updates.enableApplyStandards !== undefined)
          pascalInput.EnableApplyStandards = updates.enableApplyStandards;
        if (updates.conditions !== undefined) pascalInput.Conditions = updates.conditions;

        await updateTemplateScreen(screenId, pascalInput);

        // Optimistically update local state
        setScreens((prev) =>
          prev.map((screen) => (screen.id === screenId ? { ...screen, ...updates } : screen))
        );

        // Queue a deferred UILM sync — only when a translatable field
        // changed. Skipping unrelated edits (e.g. flipping
        // enableApplyStandards) keeps the queue empty for non-text
        // toggles. The actual sync runs ~1500ms after the last edit
        // settles via `flushPendingScreenUilm`.
        const touchedTranslatable =
          updates.title !== undefined || updates.description !== undefined;
        if (!template.organizationId && touchedTranslatable) {
          pendingScreenUilmIds.current.add(screenId);
          flushPendingScreenUilm();
        }
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_SAVECHANGES') });
        await refreshScreens();
      } finally {
        setSaving(false);
        setLastSaved(new Date());
      }
    },
    [
      template.organizationId,
      refreshScreens,
      t,
      toast,
      flushPendingScreenUilm,
    ]
  );

  // Add a new screen
  const addScreen = useCallback(async () => {
    setSaving(true);
    try {
      const newTitle = t('TEMPLATEBUILDER_BUILDER_SCREENTITLE', { index: screens.length + 1 });
      const result = await createTemplateScreen({
        TemplateId: template.id,
        Title: newTitle,
        Description: '',
        Order: screens.length + 1,
        Type: 'standard',
      });

      const newScreenId = result?.insertTemplateScreens?.itemId;

      // UILM sync — system templates only, soft-fail.
      if (!template.organizationId && newScreenId) {
        try {
          await syncScreenSave({
            templateSlug: template.slug,
            screenId: newScreenId,
            title: newTitle,
            description: '',
          });
        } catch (uilmErr) {
          console.warn('UILM sync deferred for new screen:', uilmErr);
        }
      }

      await refreshScreens();

      if (newScreenId) {
        setSelection({
          type: 'screen',
          screenId: newScreenId,
          fieldId: null,
        });
      }
      toast({ variant: 'success', title: t('TEMPLATEBUILDER_TOASTS_SCREENADDED') });
    } catch (error) {
      toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_ADDSCREEN') });
    } finally {
      setSaving(false);
    }
  }, [
    template.id,
    template.slug,
    template.organizationId,
    screens.length,
    refreshScreens,
    t,
    toast,
  ]);

  // Add a new field to a screen
  const addField = useCallback(
    async (screenId: string) => {
      setSaving(true);
      // Generate a template-wide-unique name from the freshest state plus any
      // names reserved by still-in-flight adds (the rapid-click guard). Reserve
      // it immediately so a concurrent add picks the next one.
      const used = collectFieldNames(screensRef.current);
      reservedFieldNamesRef.current.forEach((n) => used.add(n));
      const newName = nextUniqueFieldName(used);
      reservedFieldNamesRef.current.add(newName);
      try {
        const screen = screensRef.current.find((s) => s.id === screenId);
        // Order off the max (not count) so deletions don't reintroduce a
        // collision; reservation already guarantees the name is unique.
        const orders = screen?.fields.map((f) => f.order) ?? [];
        const fieldOrder = (orders.length ? Math.max(...orders) : 0) + 1;

        const newLabel = t('TEMPLATEBUILDER_BUILDER_NEWFIELDLABEL');
        const result = await createTemplateField({
          ScreenId: screenId,
          Name: newName,
          Label: newLabel,
          Type: 'text',
          Required: false,
          Order: fieldOrder,
        });

        const newFieldId = result?.insertTemplateFields?.itemId;

        // UILM sync — system templates only, soft-fail.
        if (!template.organizationId && newFieldId) {
          try {
            await syncFieldSave({
              templateSlug: template.slug,
              screenId,
              fieldId: newFieldId,
              label: newLabel,
              helpText: null,
              placeholder: null,
              type: 'text',
              options: [],
            });
          } catch (uilmErr) {
            console.warn('UILM sync deferred for new field:', uilmErr);
          }
        }

        await refreshScreens();

        if (newFieldId) {
          setSelection({
            type: 'field',
            screenId,
            fieldId: newFieldId,
          });
        }
        toast({ variant: 'success', title: t('TEMPLATEBUILDER_TOASTS_FIELDADDED') });
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_ADDFIELD') });
      } finally {
        // refreshScreens has folded the new field (with its reserved name) into
        // state, so the reservation is no longer needed.
        reservedFieldNamesRef.current.delete(newName);
        setSaving(false);
      }
    },
    [template.slug, template.organizationId, refreshScreens, t, toast]
  );

  // Delete a screen
  const deleteScreen = useCallback(
    async (screenId: string) => {
      setSaving(true);
      try {
        await deleteTemplateScreen(screenId, true);

        await refreshScreens();

        // Update selection
        const remainingScreens = screens.filter((s) => s.id !== screenId);
        if (remainingScreens.length > 0) {
          setSelection({
            type: 'screen',
            screenId: remainingScreens[0].id,
            fieldId: null,
          });
        } else {
          setSelection({ type: null, screenId: null, fieldId: null });
        }
        toast({ variant: 'success', title: t('TEMPLATEBUILDER_TOASTS_SCREENDELETED') });
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_DELETESCREEN') });
      } finally {
        setSaving(false);
      }
    },
    [screens, refreshScreens, t, toast]
  );

  // Delete a field
  const deleteField = useCallback(
    async (fieldId: string) => {
      setSaving(true);
      try {
        await deleteTemplateField(fieldId, true);

        await refreshScreens();

        // Update selection to parent screen
        if (selection.fieldId === fieldId && selection.screenId) {
          setSelection({
            type: 'screen',
            screenId: selection.screenId,
            fieldId: null,
          });
        }
        toast({ variant: 'success', title: t('TEMPLATEBUILDER_TOASTS_FIELDDELETED') });
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_DELETEFIELD') });
      } finally {
        setSaving(false);
      }
    },
    [selection.fieldId, selection.screenId, refreshScreens, t, toast]
  );

  // Reorder screens
  const reorderScreens = useCallback(
    async (screenIds: string[]) => {
      const reorderedScreens = screenIds
        .map((id) => screens.find((s) => s.id === id)!)
        .filter(Boolean);
      setScreens(reorderedScreens);

      try {
        // Update each screen's order via UDS
        await Promise.all(
          screenIds.map((id, index) => updateTemplateScreen(id, { Order: index + 1 }))
        );
        await refreshScreens();
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_REORDERSCREENS') });
        await refreshScreens();
      }
    },
    [screens, refreshScreens, t, toast]
  );

  // Reorder fields within a screen
  const reorderFields = useCallback(
    async (screenId: string, fieldIds: string[]) => {
      // Optimistically update
      setScreens((prev) =>
        prev.map((screen) => {
          if (screen.id !== screenId) return screen;
          const reorderedFields = fieldIds
            .map((id) => screen.fields.find((f) => f.id === id)!)
            .filter(Boolean);
          return { ...screen, fields: reorderedFields };
        })
      );

      try {
        // Update each field's order via UDS
        await Promise.all(
          fieldIds.map((id, index) => updateTemplateField(id, { Order: index + 1 }))
        );
        await refreshScreens();
      } catch (error) {
        toast({ variant: 'destructive', title: t('TEMPLATEBUILDER_ERRORS_REORDERFIELDS') });
        await refreshScreens();
      }
    },
    [refreshScreens, t, toast]
  );

  const contextValue: BuilderContextType = {
    template,
    screens,
    selection,
    setSelection,
    refreshScreens,
    updateField: updateFieldHandler,
    updateScreen: updateScreenHandler,
    addScreen,
    addField,
    deleteScreen,
    deleteField,
    reorderScreens,
    reorderFields,
    saving,
  };

  return (
    <BuilderContext.Provider value={contextValue}>
      {/* h-[calc(100dvh-65px)] = actual viewport minus MainLayout's Header
          (h-16 + border-b = 65px). `dvh` keeps the bottom of the page
          (sticky "Add Screen" / "Add Field" footers) above mobile browser
          chrome instead of being clipped by body overflow:hidden. */}
      <div className="h-[calc(100dvh-65px)] flex flex-col bg-[hsl(var(--bg))]">
        <BuilderHeader
          lastSaved={lastSaved}
          publishing={publishing}
          hasUnpublished={hasUnpublished}
          onPublishUilm={handlePublishUilm}
          onPreviewClick={() => {
            if (previewToken) {
              const previewUrl = `/templates/${template.slug}/generate?preview=${previewToken}`;
              window.open(previewUrl, '_blank');
            } else {
              setPreviewDialogOpen(true);
            }
          }}
          // Context-sensitive back: on mobile L2/L3 it pops one layer, on L1
          // (and on desktop, where mobileView stays at 'screens') it falls
          // through to BuilderHeader's default `navigate(-1)`.
          onBack={
            mobileView === 'canvas'
              ? () => setMobileView('fields')
              : mobileView === 'fields'
                ? () => setMobileView('screens')
                : undefined
          }
          // Header title follows the current mobile layer; on L1 / desktop
          // it falls back to template.title via BuilderHeader's default.
          title={
            mobileView === 'canvas'
              ? selectedField?.label || selectedScreen?.title
              : mobileView === 'fields'
                ? selectedScreen?.title
                : undefined
          }
        />

        {/* Desktop: classic 3-panel side-by-side layout */}
        <div className="hidden md:flex flex-1 overflow-hidden">
          <NavigationPanel />
          <PreviewCanvas
            selectedScreen={selectedScreen || null}
            selectedField={selectedField || null}
            onOpenProperties={() => setPropsSheetOpen(true)}
          />
          <PropertiesPanel
            selectedScreen={selectedScreen || null}
            selectedField={selectedField || null}
            allScreens={screens}
          />
        </div>

        {/* Mobile: 3-layer wizard (chat-app style) — one page at a time */}
        <div className="flex md:hidden flex-1 flex-col min-h-0 overflow-hidden">
          {mobileView !== 'canvas' && (
            <NavigationPanel
              isMobile
              mobileView={mobileView}
              onMobileViewChange={setMobileView}
              onDrillToCanvas={() => setMobileView('canvas')}
            />
          )}
          {mobileView === 'canvas' && (
            <PreviewCanvas
              selectedScreen={selectedScreen || null}
              selectedField={selectedField || null}
              onOpenProperties={() => setPropsSheetOpen(true)}
            />
          )}
        </div>
      </div>

      {/* Mobile: Properties bottom sheet */}
      <Sheet open={propsSheetOpen} onOpenChange={setPropsSheetOpen}>
        <SheetContent
          side="bottom"
          hideClose
          className="md:hidden h-[85dvh] p-0 rounded-t-2xl flex flex-col"
        >
          <SheetTitle className="sr-only">Properties</SheetTitle>
          {/* Drag-handle indicator */}
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted shrink-0" />
          <div className="flex-1 min-h-0 overflow-hidden">
            <PropertiesPanel
              selectedScreen={selectedScreen || null}
              selectedField={selectedField || null}
              allScreens={screens}
              isMobile
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Preview Dialog */}
      <PreviewDialog
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
        templateId={template.id}
        templateSlug={template.slug}
        locale={locale}
        previewToken={previewToken}
        onTokenGenerated={(token) => {
          setPreviewToken(token);
          // Auto-open preview after generation
          const previewUrl = `/templates/${template.slug}/generate?preview=${token}`;
          window.open(previewUrl, '_blank');
        }}
      />
    </BuilderContext.Provider>
  );
}
